import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, PenLine, Scale, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const CURSIVE_STYLES = {
  vibes:   { fontFamily: "'Great Vibes', cursive" },
  dancing: { fontFamily: "'Dancing Script', cursive", fontWeight: 600 },
  alex:    { fontFamily: "'Alex Brush', cursive" },
};
const STYLE_LABELS = { vibes: "Elegante", dancing: "Fluido", alex: "Sofisticado" };

function renderSignedContent(content, signerName, style = "vibes") {
  if (!content) return null;
  const lines = content.split("\n");
  const nodes = [];
  const roleRe = /^\s*(CONTRATANTE|OUTORGANTE|DECLARANTE)[\s:]/i;
  const undRe = /^_{5,}\s*$/;
  const cursiveStyle = { ...CURSIVE_STYLES[style] || CURSIVE_STYLES.vibes, fontSize: "28px", textAlign: "center", lineHeight: 1.1, color: "#0f172a", marginTop: "12px" };
  let replaced = false;
  for (let i = 0; i < lines.length; i++) {
    const s = lines[i];
    if (!replaced && signerName && undRe.test(s.trim())) {
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j++;
      if (j < lines.length && roleRe.test(lines[j])) {
        nodes.push(<div key={`s-${i}`} style={cursiveStyle}>{signerName}</div>);
        replaced = true;
        continue;
      }
    }
    nodes.push(<div key={i} style={{ whiteSpace: "pre-wrap" }}>{s || "\u00A0"}</div>);
  }
  return nodes;
}

export default function SignPublic() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/public/documents/${token}`);
      setDoc(r.data);
      if (r.data.signer_name) setName(r.data.signer_name);
    } catch (e) {
      setError(e?.response?.data?.detail || "Link inválido");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]);

  const [waSent, setWaSent] = useState(false);
  const [style, setStyle] = useState("vibes");
  const sign = async () => {
    if (!name.trim() || !accepted) return;
    setSigning(true);
    try {
      await api.post(`/public/documents/${token}/sign`, { signer_name: name, signer_cpf: cpf, signer_style: style });
      await load();
      const confirmMsg = encodeURIComponent(
        `✅ Confirmação de assinatura eletrônica\n\n` +
        `Documento: ${doc.title}\n` +
        `Assinado por: ${name}\n` +
        `Em: ${new Date().toLocaleString('pt-BR')}\n\n` +
        `Você pode reabrir o documento: ${window.location.origin}/sign/${token}\n` +
        `Verificar autenticidade: ${window.location.origin}/verify/${token}`
      );
      window.open(`https://wa.me/?text=${confirmMsg}`, "_blank");
      setWaSent(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao assinar");
    } finally { setSigning(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2"/>Carregando...</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="max-w-md bg-white p-8 rounded-2xl border border-slate-100 text-center">
        <div className="mx-auto w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-3"><ShieldCheck className="w-5 h-5 text-red-600"/></div>
        <div className="font-heading text-xl font-semibold text-slate-900">Não foi possível abrir</div>
        <div className="text-sm text-slate-500 mt-1">{error}</div>
      </div>
    </div>
  );

  const signed = !!doc.signed_at;
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white ring-1 ring-slate-200 flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="Del Padre Advocacia" className="w-full h-full object-contain p-0.5"/>
          </div>
          <div>
            <div className="font-heading text-base font-semibold text-slate-900">Hermes · Assinatura</div>
            <div className="text-xs text-slate-500">{doc.lawyer_escritorio || doc.lawyer_name || "Documento para assinatura"}</div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h1 className="font-heading text-2xl font-semibold text-slate-900">{doc.title}</h1>
            {signed && (
              <span className="text-xs px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5"/> Assinado
              </span>
            )}
          </div>
          <div className="bg-slate-100 rounded-2xl p-4 border border-slate-100">
            <div className="doc-page">
              <div className="font-doc">{renderSignedContent(doc.content, signed ? doc.signer_name : "", doc.signer_style || style)}</div>
              {signed && (
                <div className="mt-10 pt-6 border-t border-slate-200 text-sm font-doc">
                  <div><b>Assinado eletronicamente por:</b> {doc.signer_name}</div>
                  <div className="text-slate-500 text-xs mt-1">Em {new Date(doc.signed_at).toLocaleString('pt-BR')}</div>
                  <div className="text-slate-400 text-xs mt-3">
                    Verifique a autenticidade em <a href={`/verify/${token}`} className="underline text-[#0f4c5c]">{window.location.origin}/verify/{token}</a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <aside className="lg:sticky lg:top-8 h-fit">
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-heading text-lg font-semibold mb-1">{signed ? "Assinatura confirmada" : "Confirme sua assinatura"}</h2>
            <p className="text-xs text-slate-500 mb-4">
              {signed ? "Este documento já foi assinado eletronicamente." : "Confirme seu nome completo. Registraremos data, hora e este consentimento."}
            </p>
            {!signed && (
              <div className="space-y-4">
                <label className="block">
                  <div className="text-xs font-medium text-slate-600 mb-1">Nome completo</div>
                  <input value={name} onChange={(e) => setName(e.target.value)} data-testid="sign-name"
                    className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm"/>
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-slate-600 mb-1">CPF (opcional)</div>
                  <input value={cpf} onChange={(e) => setCpf(e.target.value)} data-testid="sign-cpf"
                    className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm"/>
                </label>
                <label className="flex items-start gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="sign-accept" className="mt-0.5"/>
                  <span>Li o conteúdo integral do documento acima e manifesto expressa aceitação, ciente de que esta manifestação constitui assinatura eletrônica nos termos do art. 10, §2º, MP 2.200-2/2001.</span>
                </label>
                {name.trim() && (
                  <div>
                    <div className="text-xs font-medium text-slate-600 mb-2">Estilo da sua assinatura</div>
                    <div className="grid grid-cols-3 gap-2">
                      {Object.keys(CURSIVE_STYLES).map(k => (
                        <button key={k} type="button" onClick={() => setStyle(k)} data-testid={`style-${k}`}
                          className={`h-14 rounded-lg border transition ${style===k?'border-slate-900 bg-slate-50 shadow-sm':'border-slate-200 bg-white hover:border-slate-400'}`}>
                          <div style={{ ...CURSIVE_STYLES[k], fontSize: 22, color: "#0f172a", lineHeight: 1 }}>{name.split(" ")[0]}</div>
                          <div className="text-[10px] text-slate-500 mt-0.5">{STYLE_LABELS[k]}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <button onClick={sign} disabled={!name.trim() || !accepted || signing} data-testid="btn-sign"
                  className="w-full gradient-primary text-white h-11 rounded-xl inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition text-sm font-medium disabled:opacity-60">
                  <PenLine className="w-4 h-4"/> {signing ? "Confirmando..." : "Assinar documento"}
                </button>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
